<template>
  <div
    :class="readinessView ? 'readiness-view-proficiency-badge':''"
    class="ex-readiness-badge"
    :role="showBadgeStatusModel ? 'group' : null"
    :aria-label="showBadgeStatusModel ? badgeStatusTooltip.title : null"
    :data-badge-status="showBadgeStatusModel ? resolvedBadgeStatus : null"
    :data-issuing-method="showBadgeStatusModel ? issuingMethodKey : null"
    :data-counts-toward-readiness="showBadgeStatusModel ? String(badgeCountsTowardReadiness) : null"
  >
    <v-chip v-if="plain === false && mini === false && !firstName && !lastName" :class="classes">
      <template v-if="showProficiencyStatusDot">
        <EXTooltip
          v-if="isAppointedDot"
          class="ex-readiness-badge__appointed-tooltip"
          placement="top"
          title="Appointed by an administrator"
          :delay="200"
        >
          <svg
            :class="appointedDiamondClasses"
            class="ex-readiness-badge__appointed-diamond prop-custom-bedge"
            viewBox="0 0 10 10"
            width="10"
            height="10"
            role="img"
            aria-label="Appointed"
            focusable="false"
          >
            <path d="M5 0L10 5L5 10L0 5Z" fill="currentColor" />
          </svg>
        </EXTooltip>
        <template v-else-if="showAnimatedStandingDots">
          <PulsatingRedDot
            v-if="isObsoleteCurrentBadge"
            class="ex-readiness-badge__pulse-dot prop-custom-bedge"
            :size="14"
            :tier="4"
            aria-label="Obsolete"
          />
          <span
            v-else-if="dotForUi === 'Given'"
            class="ex-readiness-badge__given-square prop-custom-bedge"
            role="img"
            aria-label="Given"
          />
          <v-badge v-else :color="getStatusColor" dot class="prop-custom-bedge"></v-badge>
        </template>
        <v-badge v-else :color="getStatusColor" dot class="prop-custom-bedge"></v-badge>
      </template>
      <EXTooltip
          placement="top"
          :title="getTooltipValue"
          :description="badgeStatusDescription"
          :delay="500"
      >
        <span :class="textClasses" class="prop-status-label" v-if="!acting_as_coach && !acting_as_architect">{{ levelLabel }}</span>
        <span :class="textClasses" class="prop-status-label" v-if="acting_as_coach">Acting as {{ levelLabel }}</span>
        <span :class="textClasses" class="prop-status-label" v-if="acting_as_architect">Acting as {{ levelLabel }}</span>
      </EXTooltip>
      <span v-if="view === 'Editable' && level === 'Novice' && showNoviceButton" class="novice-btn" @click="noviceBadge(level)">
        <img :src="require(`@components/assets/novicePlusIcon.svg`)" class="novice-plus" alt="icon">
        <img :src="require(`@components/assets/noviceProficiency.svg`)" class="novice-badge" alt="icon">
      </span>
      <div v-if="(view === 'Editable' || view == 'Upgraded') && level !== 'Novice'" :class="`${!upgradedVariantIcon ? 'ex-badge-icon' : 'badge-icon'}`">
        <ExpireIcon
          v-if="!upgradedVariantIcon && !hideIcons && showExpirationIconFeature"
          :icon="expirationIconVariant"
          :read-only="expirationIconReadOnly"
          :badgeLevel="level"
          :expirationDate="expirationDateForUi"
          :days="expiresInDaysForUi || 0"
          :badgeStatus="expirationIconBadgeStatus"
          :policy="expirationIconPolicy"
          :date-min="expirationDatePickerMin"
          :animated-standing-dots="showAnimatedStandingDots"
          @save="handleExpirationSave"
          @remove="handleExpirationRemove"
          @setToGoodStanding="handleSetToGoodStanding"
          @makeObsolete="handleMakeObsolete"
        />
        <EXTooltip
          v-else-if="upgradedVariantIcon && !hideIcons"
          placement="bottom"
          :title="`${upgradedVariantIcon}`"
        >
          <v-icon class="ml-2" style="max-height: 16px; max-width: 16px">{{ `$ex-proficiency-badge-${upgradedVariantIcon}` }}</v-icon>
        </EXTooltip>
        <ChatterIcon v-if="!hideChatterIcon" :unread="unread" :is_white="isBlackBackground" class="readiness-chatter-icon" @onClick="onChatterClick" />
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi === 'Obsolete' && level !== 'Novice' && showExpirationIcon && has_expiration_if_obsolete" class="ex-badge-icon">
        <ExpireIcon
          v-if="showExpirationIconFeature"
          icon="if_obsolete"
          :read-only="expirationIconReadOnly"
          :badgeLevel="level"
          :expirationDate="expirationDateForUi"
          :animated-standing-dots="showAnimatedStandingDots"
        />
        <ChatterIcon v-if="!hideChatterIcon" :unread="unread" :is_white="isBlackBackground" hover class="readiness-chatter-icon" @onClick="onChatterClick" />
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi === 'Obsolete' && level !== 'Novice' && showAboutExpirationIcon && has_expiration_if_obsolete && showExpirationIconFeature" class="badge-icon">
        <EXTooltip
          placement="bottom"
          :title="`Is about to expire ${showExpirationDays} days`"
          :description="`Unless it is brought back to Good Standing, it is set to expire on ${showExpirationDate}`"
          :children="true"
        >
          <template #icon>
            <Icon name="badge_about_expire" />
          </template>
        </EXTooltip>
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi !== 'Given' && level !== 'Novice' && showExpirationIcon && !has_expiration_if_obsolete && showExpirationIconFeature" class="badge-icon">
        <EXTooltip
          placement="bottom"
          :title="`Expires in ${showExpirationDays} days`"
          :description="`It is Set to expire on ${showExpirationDate}`"
          :children="true"
        >
          <template #icon>
            <Icon name="badge_expire" />
          </template>
        </EXTooltip>
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi !== 'Given' && level !== 'Novice' && showAboutExpirationIcon && !has_expiration_if_obsolete && showExpirationIconFeature" class="badge-icon">
        <EXTooltip
          placement="bottom"
          :title="`Is about to Expire in ${showExpirationDays} days`"
          :description="`It is Set to expire on ${showExpirationDate}`"
          :children="true"
        >
          <template #icon>
            <Icon name="badge_about_expire" />
          </template>
        </EXTooltip>
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi === 'Good Standing' && level !== 'Novice' && expirationDateForUi !== null && has_expiration_if_obsolete && showExpirationIconFeature" class="badge-icon">
        <EXTooltip
          placement="bottom"
          :title="`Expires in ${showExpirationDays} days if at that time the badge is obsolete`"
          :description="`Unless it is in Good Standing at expiration date, it is set to expire on ${showExpirationDate}. Otherwise we will attempt to expire the badge every ${retries_to_expire_in_days}`"
          :children="true"
        >
          <template #icon>
            <Icon name="badge_has_expire" />
          </template>
        </EXTooltip>
      </div>
      <div v-if="view === 'ReadOnly' && level !== 'Novice' && expirationDateForUi === null && showExpirationIconFeature" class="ex-badge-icon">
        <ExpireIcon
          icon="read_only"
          :read-only="expirationIconReadOnly"
          :badgeLevel="level"
          :expirationDate="expirationDateForUi"
          :animated-standing-dots="showAnimatedStandingDots"
        />
        <ChatterIcon v-if="!hideChatterIcon" :unread="unread" :is_white="isBlackBackground" class="readiness-chatter-icon" @onClick="onChatterClick" />
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi === 'Given' && level !== 'Novice' && showExpirationIcon && !has_expiration_if_obsolete && showExpirationIconFeature" class="ex-badge-icon">
        <ExpireIcon
          :icon="expirationIconVariant"
          :read-only="expirationIconReadOnly"
          :badgeLevel="level"
          :expirationDate="expirationDateForUi"
          :days="expiresInDaysForUi || 0"
          :badgeStatus="expirationIconBadgeStatus"
          :policy="expirationIconPolicy"
          :date-min="expirationDatePickerMin"
          :animated-standing-dots="showAnimatedStandingDots"
          @save="handleExpirationSave"
          @remove="handleExpirationRemove"
          @setToGoodStanding="handleSetToGoodStanding"
          @makeObsolete="handleMakeObsolete"
        />
        <ChatterIcon v-if="!hideChatterIcon" :unread="unread" :is_white="isBlackBackground" class="readiness-chatter-icon" @onClick="onChatterClick" />
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi === 'Given' && level !== 'Novice' && showAboutExpirationIcon && !has_expiration_if_obsolete && showExpirationIconFeature" class="ex-badge-icon">
        <ExpireIcon
          icon="soon"
          :read-only="expirationIconReadOnly"
          :badgeLevel="level"
          :expirationDate="expirationDateForUi"
          :days="expiresInDaysForUi || 0"
          :badgeStatus="expirationIconBadgeStatus"
          :policy="expirationIconPolicy"
          :date-min="expirationDatePickerMin"
          :animated-standing-dots="showAnimatedStandingDots"
        />
        <ChatterIcon v-if="!hideChatterIcon" :unread="unread" :is_white="isBlackBackground" hover class="readiness-chatter-icon" @onClick="onChatterClick" />
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi === 'Given' && level !== 'Novice' && showTodayExpirationIcon && !has_expiration_if_obsolete && showExpirationIconFeature" class="ex-badge-icon">
        <ExpireIcon
          icon="tonight"
          :read-only="expirationIconReadOnly"
          :badgeLevel="level"
          :expirationDate="expirationDateForUi"
          :days="expiresInDaysForUi || 0"
          :badgeStatus="expirationIconBadgeStatus"
          :policy="expirationIconPolicy"
          :date-min="expirationDatePickerMin"
          :animated-standing-dots="showAnimatedStandingDots"
        />
        <ChatterIcon v-if="!hideChatterIcon" :unread="unread" :is_white="isBlackBackground" hover class="readiness-chatter-icon" @onClick="onChatterClick" />
      </div>
      <div v-if="view === 'Expired' && level !== 'Novice' && expirationDateForUi === null" class="badge-icon">
        <EXTooltip
          placement="bottom"
          title="This Badge is expired"
          :children="true"
        >
          <template #icon>
            <Icon name="expired_icon" />
          </template>
        </EXTooltip>
      </div>
      <div v-if="view === 'Terminated' && level !== 'Novice' && expirationDateForUi === null" class="badge-icon">
        <EXTooltip
          placement="bottom"
          title="This Badge is terminated"
          :children="true"
        >
          <template #icon>
            <Icon name="terminated_icon" />
          </template>
        </EXTooltip>
      </div>
      <div v-if="view === 'Removed' && level !== 'Novice' && expirationDateForUi === null" class="badge-icon">
        <EXTooltip
          placement="bottom"
          title="This Badge is removed"
          :children="true"
        >
          <template #icon>
            <Icon name="removed_icon" />
          </template>
        </EXTooltip>
      </div>
      <EXTooltip
          placement="top"
          title="Remove"
          :delay="500"
          v-if="view === 'Editable' && level !== 'Novice' && !upgraded && !hideRemoveIcon"
              >
          <div class="d-flex align-center">
            <img
              :src="isHovering ? require(`@components/assets/badge-hover-cross-icon.svg`) : require(`@components/assets/badge-cross-icon.png`)"
              alt="icon"
              @click="removeBadge(level)"
              class="badge-cross-icon"
              :class="'hover-cross-' + levelLabel"
              @mouseover="isHovering = true"
              @mouseleave="isHovering = false"
          >
        </div>
      </EXTooltip>
      <EXTooltip
          placement="top"
          title="Remove"
          :delay="500"
          v-if="view === 'ReadOnly' && level !== 'Novice' && !upgraded && expirationDateForUi !== null && !hideRemoveIcon"
      >
        <div class="d-flex align-center">
          <img
              :src="isHovering ? require(`@components/assets/badge-hover-cross-icon.svg`) : require(`@components/assets/badge-cross-icon.png`)"
              alt="icon"
              @click="removeBadge(level)"
              class="badge-cross-icon"
              :class="'hover-cross-' + levelLabel"
              @mouseover="isHovering = true"
              @mouseleave="isHovering = false"
          >
        </div>
      </EXTooltip>
    </v-chip>
    <v-chip v-if="plain === false && mini === true" :class="miniClasses">
      <template v-if="showProficiencyStatusDot">
        <EXTooltip
          v-if="isAppointedDot"
          class="ex-readiness-badge__appointed-tooltip"
          placement="top"
          title="Appointed by an administrator"
          :delay="200"
        >
          <svg
            :class="appointedDiamondClasses"
            class="ex-readiness-badge__appointed-diamond prop-custom-bedge"
            viewBox="0 0 10 10"
            width="10"
            height="10"
            role="img"
            aria-label="Appointed"
            focusable="false"
          >
            <path d="M5 0L10 5L5 10L0 5Z" fill="currentColor" />
          </svg>
        </EXTooltip>
        <template v-else-if="showAnimatedStandingDots">
          <PulsatingRedDot
            v-if="isObsoleteCurrentBadge"
            class="ex-readiness-badge__pulse-dot prop-custom-bedge"
            :size="12"
            :tier="4"
            aria-label="Obsolete"
          />
          <span
            v-else-if="dotForUi === 'Given'"
            class="ex-readiness-badge__given-square prop-custom-bedge"
            role="img"
            aria-label="Given"
          />
          <v-badge v-else :color="getStatusColor" dot class="prop-custom-bedge"></v-badge>
        </template>
        <v-badge v-else :color="getStatusColor" dot class="prop-custom-bedge"></v-badge>
      </template>
      <span :class="textClasses" class="prop-status-label"></span>
      <span v-if="view === 'Editable' && level === 'Novice'" class="novice-btn" @click="noviceBadge(level)">
        <img :src="require(`@components/assets/novicePlusIcon.svg`)" class="novice-plus" alt="icon">
        <img :src="require(`@components/assets/noviceProficiency.svg`)" class="novice-badge" alt="icon">
      </span>
      <div v-if="view === 'Editable' && level !== 'Novice' && showExpirationIconFeature" class="ex-badge-icon">
        <ExpireIcon
          :icon="expirationIconVariant"
          :read-only="expirationIconReadOnly"
          :badgeLevel="level"
          :expirationDate="expirationDateForUi"
          :days="expiresInDaysForUi || 0"
          :badgeStatus="expirationIconBadgeStatus"
          :policy="expirationIconPolicy"
          :date-min="expirationDatePickerMin"
          :animated-standing-dots="showAnimatedStandingDots"
          @save="handleExpirationSave"
          @remove="handleExpirationRemove"
          @setToGoodStanding="handleSetToGoodStanding"
          @makeObsolete="handleMakeObsolete"
        />
        <ChatterIcon v-if="!hideChatterIcon" :unread="unread" :is_white="isBlackBackground" hover class="readiness-chatter-icon" @onClick="onChatterClick" />
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi !== 'Given' && level !== 'Novice' && expirationDateForUi === null && showExpirationIconFeature" class="ex-badge-icon">
        <ExpireIcon
          icon="add"
          :read-only="expirationIconReadOnly"
          :badgeLevel="level"
          :size="'small'"
          :expirationDate="expirationDateForUi"
          :animated-standing-dots="showAnimatedStandingDots"
        />
        <ChatterIcon v-if="!hideChatterIcon" :unread="unread" :is_white="isBlackBackground" hover class="readiness-chatter-icon" @onClick="onChatterClick" />
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi === 'Obsolete' && level !== 'Novice' && showExpirationIcon && has_expiration_if_obsolete && showExpirationIconFeature" class="badge-icon">
        <EXTooltip
          placement="bottom"
          :title="`Expires in ${showExpirationDays} days`"
          :description="`Unless it is brought back to Good Standing, it is set to expire on ${showExpirationDate}`"
          :children="true"
        >
          <template #icon>
            <Icon name="badge_has_expire" />
          </template>
        </EXTooltip>
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi === 'Obsolete' && level !== 'Novice' && showAboutExpirationIcon && has_expiration_if_obsolete && showExpirationIconFeature" class="badge-icon">
        <EXTooltip
          placement="bottom"
          :title="`Is about to expire ${showExpirationDays} days`"
          :description="`Unless it is brought back to Good Standing, it is set to expire on ${showExpirationDate}`"
          :children="true"
        >
          <template #icon>
            <Icon name="badge_about_expire" />
          </template>
        </EXTooltip>
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi !== 'Given' && level !== 'Novice' && showExpirationIcon && !has_expiration_if_obsolete && showExpirationIconFeature" class="badge-icon">
        <EXTooltip
          placement="bottom"
          :title="`Expires in ${showExpirationDays} days`"
          :description="`It is Set to expire on ${showExpirationDate}`"
          :children="true"
        >
          <template #icon>
            <Icon name="badge_expire" />
          </template>
        </EXTooltip>
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi !== 'Given' && level !== 'Novice' && showAboutExpirationIcon && !has_expiration_if_obsolete && showExpirationIconFeature" class="badge-icon">
        <EXTooltip
          placement="bottom"
          :title="`Is about to Expire in ${showExpirationDays} days`"
          :description="`It is Set to expire on ${showExpirationDate}`"
          :children="true"
        >
          <template #icon>
            <Icon name="badge_about_expire" />
          </template>
        </EXTooltip>
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi === 'Good Standing' && level !== 'Novice' && expirationDateForUi !== null && has_expiration_if_obsolete && showExpirationIconFeature" class="badge-icon">
        <EXTooltip
          placement="bottom"
          :title="`Expires in ${showExpirationDays} days if at that time the badge is obsolete`"
          :description="`Unless it is in Good Standing at expiration date, it is set to expire on ${showExpirationDate}. Otherwise we will attempt to expire the badge every ${retries_to_expire_in_days}`"
          :children="true"
        >
          <template #icon>
            <Icon name="badge_has_expire" />
          </template>
        </EXTooltip>
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi === 'Given' && level !== 'Novice' && expirationDateForUi === null && showExpirationIconFeature" class="ex-badge-icon">
        <ExpireIcon
          icon="read_only"
          :read-only="expirationIconReadOnly"
          :badgeLevel="level"
          :expirationDate="expirationDateForUi"
          :animated-standing-dots="showAnimatedStandingDots"
        />
        <ChatterIcon v-if="!hideChatterIcon" :unread="unread" :is_white="isBlackBackground" hover class="readiness-chatter-icon" @onClick="onChatterClick" />
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi === 'Given' && level !== 'Novice' && showExpirationIcon && !has_expiration_if_obsolete && showExpirationIconFeature" class="badge-icon">
        <EXTooltip
          placement="bottom"
          :title="`Expires in ${showExpirationDays} days`"
          :description="`Set to expire on ${showExpirationDate}`"
          :children="true"
        >
          <template #icon>
            <Icon name="badge_expire" />
          </template>
        </EXTooltip>
      </div>
      <div v-if="view === 'ReadOnly' && dotForUi === 'Given' && level !== 'Novice' && showAboutExpirationIcon && !has_expiration_if_obsolete && showExpirationIconFeature" class="badge-icon">
        <EXTooltip
          placement="bottom"
          :title="`Is about to expire in ${showExpirationDays} days`"
          :description="`Set to expire on ${showExpirationDate}`"
          :children="true"
        >
          <template #icon>
            <Icon name="badge_about_expire" />
          </template>
        </EXTooltip>
      </div>
      <img v-if="view === 'Editable' && level !== 'Novice' && !hideRemoveIcon" :src="require(`@components/assets/badge-cross-icon.png`)" alt="icon"
        @click="removeBadge(level)" class="badge-cross-icon">
    </v-chip>
    <v-chip v-if="plain === true && mini === false" :class="plainClasses">
      <img :src="require(`@components/assets/plainminimalistic.svg`)" alt="icon" class="plain-badge-image">
    </v-chip>
    <div
      v-if="firstName && lastName"
      :class="[holderClasses, { status: status !== 'Current' }]"
      class="pro-holder-badge"
    >
      <div class="pro-holder-badge-content">
        <EXTooltip
          :title="proficiencyDescription"
          :description="badgeStatusDescription"
          placement="top"
          :position=true
        >
          <div class="pro-holder-badge-info">
            <template v-if="showHolderStatusDot">
              <EXTooltip
                v-if="isAppointedDot"
                class="ex-readiness-badge__appointed-tooltip"
                placement="top"
                title="Appointed by an administrator"
                :delay="200"
              >
                <svg
                  :class="holderAppointedDiamondClasses"
                  class="ex-readiness-badge__appointed-diamond ex-readiness-badge__appointed-diamond--holder prop-custom-bedge-holder"
                  viewBox="0 0 10 10"
                  width="10"
                  height="10"
                  role="img"
                  aria-label="Appointed"
                  focusable="false"
                >
                  <path d="M5 0L10 5L5 10L0 5Z" fill="currentColor" />
                </svg>
              </EXTooltip>
              <template v-else-if="showAnimatedStandingDots">
                <PulsatingRedDot
                  v-if="isObsoleteCurrentBadge"
                  class="ex-readiness-badge__pulse-dot ex-readiness-badge__pulse-dot--holder prop-custom-bedge-holder"
                  :size="12"
                  :tier="4"
                  aria-label="Obsolete"
                />
                <span
                  v-else-if="dotForUi === 'Given'"
                  class="ex-readiness-badge__given-square ex-readiness-badge__given-square--holder prop-custom-bedge-holder"
                  role="img"
                  aria-label="Given"
                />
                <v-badge
                  v-else
                  :color="getStatusColor"
                  dot
                  class="prop-custom-bedge-holder"
                ></v-badge>
              </template>
              <v-badge
                v-else
                :color="getStatusColor"
                dot
                class="prop-custom-bedge-holder"
              ></v-badge>
            </template>
            <ExAvatar
              size="small"
              :picture="avatar || ''"
              :firstName="!avatar ? firstName : ''"
              :lastName="!avatar ? lastName : ''"
              class="custom-avatar"
            />
            <EXTooltip 
              v-if="isNameTruncated" 
              placement="top" 
              :title="`${firstName} ${lastName}`"
              :delay="300"
            >
              <span :class="holderTextClasses" class="holder-status-label truncated-badge-name">{{ firstName }} {{ lastName }}</span>
            </EXTooltip>
            <span v-else :class="holderTextClasses" class="holder-status-label">{{ firstName }} {{ lastName }}</span>
          </div>
        </EXTooltip>
        <ExpireIcon
          class="ex-holder-badge-icon"
          v-if="status === 'Current' && !hideIcons && showExpirationIconFeature"  
          :icon="expirationIconVariant"
          :read-only="expirationIconReadOnly"
          :badgeLevel="level"
          :expirationDate="expirationDateForUi"
          :days="expiresInDaysForUi || 0"
          :badgeStatus="expirationIconBadgeStatus"
          :policy="expirationIconPolicy"
          :date-min="expirationDatePickerMin"
          :animated-standing-dots="showAnimatedStandingDots"
          @save="handleExpirationSave"
          @remove="handleExpirationRemove"
          @setToGoodStanding="handleSetToGoodStanding"
          @makeObsolete="handleMakeObsolete"
        />
        <ChatterIcon v-if="!hideChatterIcon" :unread="unread" :is_white="isBlackBackground" class="readiness-chatter-icon" @onClick="onChatterClick" />
        <Icon :name="`ex_${status}`" v-if="status !== 'Current' && !hideIcons" />
      </div>
      <EXTooltip
        title="Remove"
        placement="top"
        :position="true"
      >
        <v-btn
          icon
          color="#98A2B3"
          class="avail-close-btn holder-cross-icon"
          :class="'hover-cross-' + levelLabel"
          @click="removeHolder(level)"
          :data-testid="'remove-badge-' + (id != null ? id : 'holder')"
          v-if="status === 'Current' && !hideRemoveIcon"
          @mouseover="isHovering = true"
          @mouseleave="isHovering = false"
        >
          <Icon :name="isHovering ? 'ex_pro_badge_holder_hover_close' : 'ex_pro_badge_holder_close'" />
        </v-btn>
      </EXTooltip>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch, getCurrentInstance, onMounted } from 'vue';
import Icon from "@components/icons/Icon.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import PulsatingRedDot from "@components/PulsatingRedDot/PulsatingRedDot.vue";
import ExpireIcon from "@components/ExpirationIcon/ExpirationIcon.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import dayjs from "dayjs";
import { getBadgeLevelDisplayLabel, isBadgeLevelValue, normalizeBadgeLevel } from "@components/utils/normalizeBadgeLevel";
import ExAvatar from "@components/common/ExAvatar.vue";
import { LD_FLAG_IDEA_MDP_632, LD_FLAG_IDEA_MDP_641 } from "@components/constants/launchDarklyFlagKeys";
import {
  BADGE_STATUS_UNKNOWN,
  buildBadgeStatusTooltip,
  countsTowardReadiness,
  getDotForBadgeStatus,
  resolveBadgeStatus,
  resolveIssuingMethod,
  wantsBadgeStatusModel,
} from "@components/utils/badgeStatus";
import {
  getExpirationPickerDateMinForEditorBadgeStatus,
  useProficiencyBadgeExpirationStore,
  wantsAnimatedStandingDots,
  wantsAppointedDot,
  wantsProficiencyExpirationIcon,
} from "../../account/src/store/pinia/proficiencyBadgeExpiration";

const instance = getCurrentInstance();
const { proxy } = instance;

const proficiencyBadgeExpiration = useProficiencyBadgeExpirationStore();

// Props definition
const props = defineProps({
  level: {
    type: [String, Number],
    default: "Novice",
  },
  dot: {
    type: String,
    default: "Good Standing",
  },
  view: {
    type: String,
    validator: function (value) {
      return ['Editable', 'ReadOnly', 'Null', 'Upgraded'].indexOf(value) !== -1;
    },
  },
  expiration_date: {
    type: Date,
    default: null
  },
  expires_in_days_from_becoming_obsolete: {
    type: Number,
    default: null
  },
  has_expiration_if_obsolete: {
    type: Boolean,
    default: false
  },
  retries_to_expire_in_days: {
    type: Number,
    default: 365
  },
  plain: {
    type: Boolean,
    default: false
  },
  mini: {
    type: Boolean,
    default: false
  },
  readinessView: {
    type: Boolean,
    default: false,
  },
  hideRemoveIcon: {
    type: Boolean,
    default: false,
  },
  hideIcons: {
    type: Boolean,
    default: false,
  },
  upgraded: {
    type: Boolean,
    default: false
  },
  upgradedVariantIcon: {
    type: String,
    validator: function (value) {
      return ['expired', 'removed', 'upgraded'].indexOf(value) !== -1;
    },
  },
  showNoviceButton: {
    type: Boolean,
    default: false,
  },
  oldLevel: {
    type: String,
    validator: function (value) {
      return isBadgeLevelValue(value);
    },
  },
  oldDot: {
    type: String,
    validator: function (value) {
      return ['Good Standing', 'Given', 'Obsolete', 'No Dot'].indexOf(value) !== -1;
    },
  },
  status: {
    type: String,
    default: 'Current',
    validator: function (value) {
      return ['Current', 'Expired', 'Terminated', 'Removed'].indexOf(value) !== -1;
    },
  },
  firstName: {
    type: String,
    default: ''
  },
  lastName: {
    type: String,
    default: ''
  },
  avatar: {
    type: String,
    default: ''
  },
  id: {
    type: [Number, String],
    default: null,
  },
  acting_as_coach: {
    type: Boolean,
    default: false
  },
  acting_as_architect: {
    type: Boolean,
    default: false
  },
  unread: {
    type: Number,
    default: 0
  },
  withoutFeatureFlag: {
    type: Boolean,
    default: false
  },
  /** When true, show animated standing dots without `idea-MDP-453` (Storybook, tests). */
  animatedStandingDotsWithoutFlag: {
    type: Boolean,
    default: false
  },
  /** When true, show the Appointed diamond dot without `idea-MDP-641` (Storybook, tests). */
  appointedDotWithoutFlag: {
    type: Boolean,
    default: false
  },
  hideChatterIcon: {
    type: Boolean,
    default: false
  },
  expirationIconReadOnly: {
    type: Boolean,
    default: false,
  },
  badgeId: {
    type: [Number, String],
    default: null
  },
  /** Badge lifecycle status key (good_standing, obsolete, expired, upgraded, revoked); the API's GOOD_STANDING form is accepted. Not the holder-row `status` prop. */
  badgeStatus: {
    type: String,
    default: null
  },
  /** Issuing method key: given | awarded. */
  issuingMethod: {
    type: String,
    default: null
  },
  endedOn: {
    type: [String, Date],
    default: null
  },
  /** Display name, or an object carrying `name` / `full_name` / `first_name` + `last_name`. */
  endedBy: {
    type: [String, Object],
    default: null
  },
  endReason: {
    type: String,
    default: null
  },
  /** When true, apply the badge status model without `idea-MDP-632` (Storybook, tests). */
  badgeStatusWithoutFlag: {
    type: Boolean,
    default: false
  },
});

// Emits definition
const emit = defineEmits([
  'removeBadge',
  'noviceBadge',
  'removeHolder',
  'expirationSynced',
  'chatter-click',  // <-- Add this
]);

function notifyExpirationSynced() {
  emit('expirationSynced');
}

watch(
  () => proficiencyBadgeExpiration.refreshGeneration,
  (n, o) => {
    if (n > (o ?? 0)) {
      notifyExpirationSynced();
    }
  }
);

function parseOverlayDateInput(v) {
  if (v == null || v === "") return null;
  if (v instanceof Date) return v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** PATCH response / optimistic fields until parent props refetch (any surface, any list API). */
const patchOverlay = computed(() =>
  proficiencyBadgeExpiration.getPatchOverlay(props.badgeId, props.id)
);

const expirationDateForUi = computed(() => {
  const o = patchOverlay.value;
  if (o && Object.prototype.hasOwnProperty.call(o, "expiration_date")) {
    const parsed = parseOverlayDateInput(o.expiration_date);
    if (parsed != null) return parsed;
    if (o.expiration_date === null) return null;
  }
  return props.expiration_date;
});

const expiresInDaysForUi = computed(() => {
  const o = patchOverlay.value;
  if (o && Object.prototype.hasOwnProperty.call(o, "expires_in_days_from_becoming_obsolete")) {
    const n = o.expires_in_days_from_becoming_obsolete;
    if (n == null || n === "") return null;
    const num = Number(n);
    return Number.isFinite(num) && num > 0 ? num : null;
  }
  const p = props.expires_in_days_from_becoming_obsolete;
  return p != null && p > 0 ? p : null;
});

// idea-MDP-641: Appointed diamond dot; with the flag off an Appointed badge reads as Good Standing.
const showAppointedDotFeature = computed(() =>
  wantsAppointedDot({
    appointedDotWithoutFlag: props.appointedDotWithoutFlag,
    ideaMdp641: proxy.flags?.[LD_FLAG_IDEA_MDP_641],
  })
);

// idea-MDP-632: status + issuing-method model; with the flag off (or no status supplied) the badge renders as before.
const resolvedBadgeStatus = computed(() => resolveBadgeStatus(props.badgeStatus));

const showBadgeStatusModel = computed(
  () =>
    resolvedBadgeStatus.value !== null &&
    wantsBadgeStatusModel({
      badgeStatusWithoutFlag: props.badgeStatusWithoutFlag,
      ideaMdp632: proxy.flags?.[LD_FLAG_IDEA_MDP_632],
    })
);

const isUnknownBadgeStatus = computed(
  () => showBadgeStatusModel.value && resolvedBadgeStatus.value === BADGE_STATUS_UNKNOWN
);

const issuingMethodKey = computed(() => resolveIssuingMethod(props.issuingMethod));

const badgeCountsTowardReadiness = computed(() => countsTowardReadiness(resolvedBadgeStatus.value));

const badgeStatusTooltip = computed(() =>
  buildBadgeStatusTooltip({
    levelLabel: levelLabel.value,
    status: resolvedBadgeStatus.value,
    issuingMethod: issuingMethodKey.value,
    endedOn: props.endedOn,
    endedBy: props.endedBy,
    endReason: props.endReason,
  })
);

const badgeStatusDescription = computed(() =>
  showBadgeStatusModel.value ? badgeStatusTooltip.value.description : undefined
);

const dotForUi = computed(() => {
  const o = patchOverlay.value;
  const baseDot = showBadgeStatusModel.value
    ? getDotForBadgeStatus(resolvedBadgeStatus.value, props.dot)
    : props.dot;
  const dot = o && o.dot ? o.dot : baseDot;
  if (dot === 'Appointed' && !showAppointedDotFeature.value) return 'Good Standing';
  return dot;
});

const isAppointedDot = computed(() => dotForUi.value === 'Appointed');

watch(
  () => [
    props.badgeId,
    props.id,
    props.expiration_date,
    props.expires_in_days_from_becoming_obsolete,
    props.dot,
  ],
  () => {
    proficiencyBadgeExpiration.reconcilePatchOverlay(props.badgeId, props.id, {
      expiration_date: props.expiration_date,
      expires_in_days_from_becoming_obsolete: props.expires_in_days_from_becoming_obsolete,
      dot: props.dot,
    });
  }
);

// Reactive data
const state = reactive({
  tooltipVisible: false
});
const isHovering = ref(false);

// ExpirationIcon (calendar UX) follows idea-MDP-11 everywhere; parents can force
// visibility with withoutFeatureFlag (e.g. Storybook, directory tables).
const showExpirationIconFeature = computed(() =>
  wantsProficiencyExpirationIcon({
    withoutFeatureFlag: props.withoutFeatureFlag,
    ideaMdp11: proxy.flags?.["idea-MDP-11"],
  })
);

// idea-MDP-453: Given square + Obsolete current pulse; parents can force via animatedStandingDotsWithoutFlag.
const showAnimatedStandingDots = computed(() =>
  wantsAnimatedStandingDots({
    animatedStandingDotsWithoutFlag: props.animatedStandingDotsWithoutFlag,
    ideaMdp453: proxy.flags?.["idea-MDP-453"],
  })
);

// Computed properties
const levelLabel = computed(() => getBadgeLevelDisplayLabel(props.level) || props.level);
const levelKey = computed(() => normalizeBadgeLevel(props.level) || String(props.level || '').toLowerCase());

const getTooltipValue = computed(() => {
  if (showBadgeStatusModel.value) {
    return badgeStatusTooltip.value.title;
  }
  if (props.view === 'Removed' && props.level !== 'Novice' && expirationDateForUi.value === null) {
    return `${levelLabel.value} in ${dotForUi.value} Removed`;
  } else if (props.view === 'Expired' && props.level !== 'Novice' && expirationDateForUi.value === null && dotForUi.value !== "Obsolete") {
    return `${levelLabel.value} in ${dotForUi.value} Expired`;
  } else if (props.view === 'Expired' && props.level !== 'Novice' && expirationDateForUi.value === null && dotForUi.value === "Obsolete") {
    return `${levelLabel.value} ${dotForUi.value} Expired`;
  } else if (props.upgraded && props.level !== 'Novice' && props.upgradedVariantIcon === "upgraded") {
    return `Upgraded to ${levelLabel.value} ${dotForUi.value}`;
  } else {
    let string = levelLabel.value;
    if (dotForUi.value !== "No Dot") {
      string += ' '
      string += dotForUi.value
    }
    return string;
  }
});

const getStatusColor = computed(() => {
  switch (dotForUi.value) {
    case 'Good Standing':
      return '#17B26A';
    case 'Given':
      return '#2C2C2C';
    case 'Unknown':
      return '#667085';
    default:
      return '#FF0303';
  }
});

const showProficiencyStatusDot = computed(
  () => dotForUi.value !== 'No Dot' && props.level !== 'Novice'
);

/** Holder row historically showed the dot whenever dot ≠ No Dot (no Novice guard). */
const showHolderStatusDot = computed(() => dotForUi.value !== 'No Dot');

/** Pulsating standing dot only when the badge is obsolete and still current (ER-9119). */
const isObsoleteCurrentBadge = computed(
  () => dotForUi.value === 'Obsolete' && props.status === 'Current'
);

const isBlackBackground = computed(() => {
  return ["Expired", "Removed", "Terminated"].includes(props.view) || props.upgraded;
});

const classes = computed(() => {
  const levelClass = isUnknownBadgeStatus.value
    ? "proficiency-badge--neutral"
    : `proficiency-badge--${levelLabel.value}`;
  return {
    [levelClass]: true,
    "black-background": isBlackBackground.value,
  };
});

const holderClasses = computed(() => {
  return {
    [`holder-badge--${levelLabel.value}`]: true,
  };
});

const miniClasses = computed(() => {
  const levelLower = props.level ? levelKey.value : 'novice';
  return {
    [`proficiency-badge-mini--${levelLower}`]: true,
    [`proficiency-badge-mini`]: true,
  };
});

const plainClasses = computed(() => {
  if (props.level && props.level.length > 0) {
    return {
      [`proficiency-badge-plain--${levelKey.value}`]: true,
    };
  }
  else {
    return {}
  }
});

const textClasses = computed(() => {
  const textLevelClass = isUnknownBadgeStatus.value
    ? "proficiency-text--neutral"
    : `proficiency-text--${levelLabel.value}${props.upgraded ? '__upgraded' : ''}`;
  return {
    [textLevelClass]: true,
    'ml-3': (props.level !== 'Novice' && dotForUi.value !== 'No Dot' && !isAppointedDot.value),
    'ex-readiness-badge__label--appointed': isAppointedDot.value && props.level !== 'Novice',
  };
});

const appointedDiamondClasses = computed(() => ({
  [`proficiency-text--${levelLabel.value}${props.upgraded ? '__upgraded' : ''}`]: true,
}));

const holderAppointedDiamondClasses = computed(() => ({
  [`holder-text--${levelLabel.value}`]: true,
}));

const isNameTruncated = computed(() => {
  const fullName = `${props.firstName} ${props.lastName}`.trim();
  return fullName.length > 20; // Show tooltip if name is longer than 20 characters
});

const holderTextClasses = computed(() => {
  return {
    [`holder-text--${levelLabel.value}`]: true,
  };
});

const showExpirationIcon = computed(() => {
  const today = dayjs();
  const exp = expirationDateForUi.value;
  if (exp == null) return false;
  const expirationDateMinus30Days = dayjs(exp).subtract(30, 'days');
  return (
    exp !== null &&
    today.isBefore(expirationDateMinus30Days)
  );
});

const showExpirationDays = computed(() => {
  const today = dayjs();
  const expirationDate = dayjs(expirationDateForUi.value);
  const diffInDays = expirationDate.diff(today, 'day');
  return diffInDays;
});

const showExpirationDate = computed(() => {
  return dayjs(expirationDateForUi.value).format("DD-MMMM-YYYY");
});

const showAboutExpirationIcon = computed(() => {
  const today = dayjs();
  const exp = expirationDateForUi.value;
  if (exp == null) return false;
  const expirationDateMinus30Days = dayjs(exp).subtract(30, 'days');
  return (
    exp !== null &&
    today.isAfter(expirationDateMinus30Days) &&
    today.isBefore(exp)
  );
});

const showTodayExpirationIcon = computed(() => {
  const today = new Date();
  const expirationDate = new Date(expirationDateForUi.value);
  today.setHours(0, 0, 0, 0);
  expirationDate.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((expirationDate - today) / (1000 * 60 * 60 * 24));
  return (
    diffDays === 0
  );
});

const proficiencyDescription = computed(() => {
  if (showBadgeStatusModel.value) {
    return badgeStatusTooltip.value.title;
  }
  switch (levelKey.value) {
    case 'apprentice':
      return `Apprentice ${dotForUi.value}`;
    case 'proficient':
      return `Proficient ${dotForUi.value}`;
    case 'coach':
      return `Coach ${dotForUi.value}`;
    case 'architect':
      return `Architect ${dotForUi.value}`;
    default:
      return '';
  }
});

const expirationIconVariant = computed(() => {
  if (expiresInDaysForUi.value !== null && expiresInDaysForUi.value > 0) {
    return 'if_obsolete';
  }

  if (dotForUi.value === 'Given' || dotForUi.value === 'Good Standing' || dotForUi.value === 'Obsolete') {
    if (expirationDateForUi.value === null) {
      // ER-9119: with MDP-453 UX, obsolete + no date uses if_obsolete calendar (red marker), not plain "add".
      if (dotForUi.value === 'Obsolete' && showAnimatedStandingDots.value) {
        return 'if_obsolete';
      }
      return 'add';
    }
    return 'edit';
  }

  return 'add';
});

const expirationIconPolicy = computed(() => {
  if (expiresInDaysForUi.value !== null && expiresInDaysForUi.value > 0) {
    return 'Days';
  }
  
  if (expirationDateForUi.value !== null) {
    return 'Date';
  }
  
  return 'Date';
});

const expirationIconBadgeStatus = computed(() => {
  return dotForUi.value;
});

/** Same rule as ExpirationDateEditor `badgeStatus` (Given / Obsolete vs other). */
const expirationDatePickerMin = computed(() =>
  getExpirationPickerDateMinForEditorBadgeStatus(expirationIconBadgeStatus.value)
);

const removeBadge = ($event) => {
  const levelStr = typeof $event === "string" ? $event : props.level;
  const bid = props.badgeId ?? props.id;
  if (bid != null && bid !== "") {
    // Keep badge removal flow safe by clearing expiration first on that specific badge.
    void proficiencyBadgeExpiration.clearExpiration(props.badgeId, props.id);
    emit("removeBadge", {
      id: bid,
      level: levelStr,
      currentBadge: { id: bid, level: levelStr },
    });
    return;
  }
  emit("removeBadge", $event);
};

const noviceBadge = ($event) => {
  emit('noviceBadge', $event);
};

const removeHolder = ($event) => {
  // Emit both removeHolder (for backward compatibility) and removeBadge (for new flow)
  emit('removeHolder', $event);
  // Emit removeBadge event - the parent (EmployeeBadgeHolderList) will pass the full badge data
  // We just need to trigger the event, the parent handles the data
  emit('removeBadge');
};

const handleExpirationSave = async (data) => {
  await proficiencyBadgeExpiration.saveExpirationFromEditor(props.badgeId, props.id, data);
};

const handleExpirationRemove = async () => {
  await proficiencyBadgeExpiration.clearExpiration(props.badgeId, props.id);
};

const handleSetToGoodStanding = async () => {
  await proficiencyBadgeExpiration.restoreGoodStanding(props.badgeId, props.id);
};

const handleMakeObsolete = async () => {
  await proficiencyBadgeExpiration.markObsolete(props.badgeId, props.id);
};
// Handler to emit chatter-click
const onChatterClick = (event) => {
  emit('chatter-click', {
    level: props.level,
    dot: dotForUi.value,
    view: props.view,
    expiration_date: expirationDateForUi.value,
    badgeProps: { ...props },
  });
};
// Initialize feature flags (idea-MDP-11: expiration icon; idea-MDP-453: standing dots UX)
onMounted(async () => {
  try {
    await proxy.$watchFlag('idea-MDP-11', false);
  } catch (error) {
    console.error('Error loading idea-MDP-11 feature flag:', error);
  }
  try {
    await proxy.$watchFlag('idea-MDP-453', false);
  } catch (error) {
    console.error('Error loading idea-MDP-453 feature flag:', error);
  }
  try {
    await proxy.$watchFlag(LD_FLAG_IDEA_MDP_641, false);
  } catch (error) {
    console.error('Error loading idea-MDP-641 feature flag:', error);
  }
  try {
    await proxy.$watchFlag(LD_FLAG_IDEA_MDP_632, false);
  } catch (error) {
    console.error('Error loading idea-MDP-632 feature flag:', error);
  }
});

</script>

<style>
.ex-readiness-badge {
  display: flex;
  align-items: center;
  position: relative;
}

.ex-readiness-badge .v-chip {
  overflow: visible !important;
}

.ex-readiness-badge .v-chip .v-chip__content {
  overflow: visible !important;
}

.ex-readiness-badge .proficiency-badge--Apprentice {
  border-radius: 16px !important;
  border: 1px solid !important;
  border-color:#E58700 !important;
  background-color: #FFF5D3 !important;
}

.ex-readiness-badge .proficiency-badge--Proficient {
  border-radius: 16px !important;
  border: 1px solid !important;
  border-color: #00B813 !important;
  background-color: #F3FFF9 !important;
}

.ex-readiness-badge .proficiency-badge--Coach {
  border-radius: 16px !important;
  border: 1px solid !important;
  border-color: #00A3FF !important;
  background-color: #EFFEFF !important;
}

.ex-readiness-badge .proficiency-badge--Architect {
  border-radius: 16px !important;
  border: 1px solid !important;
  border-color: #2E1EC1 !important;
  background-color: #F2F3FF !important;
}

/* !important mirrors the sibling level rules; the Vuetify chip theme would otherwise win. */
.ex-readiness-badge .proficiency-badge--neutral {
  border-radius: 16px !important;
  border: 1px solid !important;
  border-color: #667085 !important;
  background-color: #F2F4F7 !important;
}

.ex-readiness-badge .proficiency-badge--Novice {
  padding: 2px 18px 2px 18px;
  height: 32px !important;
  border-radius: 6px !important;
  border: 1px solid #D0D5DD;
  background-color: #FF0303 !important;
  box-shadow: 0px 1px 2px 0px #1018280D;
}

.ex-readiness-badge .black-background {
  background-color: #666666 !important;
  border-color: black !important;
}

.ex-readiness-badge .proficiency-text--Apprentice {
  color: #E58700 !important;
}

.ex-readiness-badge .proficiency-text--Proficient {
  color: #00B813 !important;
}

.ex-readiness-badge .proficiency-text--Coach {
  color: #00A3FF !important;
}

.ex-readiness-badge .proficiency-text--Architect {
  color: #2E1EC1 !important;
}

.ex-readiness-badge .proficiency-text--neutral {
  color: #344054 !important;
}

.ex-readiness-badge .proficiency-text--Novice {
  margin-left: 0px !important;
  color: #FFFFFF !important;
}

.ex-readiness-badge .proficiency-text--Apprentice__upgraded {
  color: #FFB346 !important;
}

.ex-readiness-badge .proficiency-text--Proficient__upgraded {
  color: #00FF19 !important;
}

.ex-readiness-badge .proficiency-text--Coach__upgraded {
  color: #8DD6FF !important;
}

.ex-readiness-badge .proficiency-text--Architect__upgraded {
  color: #768DFF !important;
}

.ex-readiness-badge .prop-custom-bedge.v-badge--dot .v-badge__badge {
  width: 6px;
  height: 6px;
  inset: auto auto calc(100% - 3px) calc(100% - 0px) !important;
}

.ex-readiness-badge .ex-readiness-badge__given-square.prop-custom-bedge {
  display: inline-block;
  width: 6px;
  height: 6px;
  min-width: 6px;
  box-sizing: border-box;
  background-color: #000000;
  border-radius: 0;
  flex-shrink: 0;
  align-self: center;
}

.ex-readiness-badge .ex-readiness-badge__appointed-tooltip {
  display: inline-flex;
  flex-shrink: 0;
  align-self: center;
  line-height: 0;
}

.ex-readiness-badge .ex-readiness-badge__appointed-diamond {
  display: block;
  width: 10px;
  height: 10px;
  min-width: 10px;
  flex-shrink: 0;
}

.ex-readiness-badge .ex-readiness-badge__label--appointed {
  margin-left: 6px;
}

.ex-readiness-badge .proficiency-badge-mini .ex-readiness-badge__appointed-tooltip {
  position: relative;
  left: 2px;
}

.ex-readiness-badge .ex-readiness-badge__pulse-dot.prop-custom-bedge {
  display: inline-flex;
  flex-shrink: 0;
  align-self: center;
  line-height: 0;
  overflow: visible;
}

.ex-readiness-badge .ex-readiness-badge__pulse-dot .pulsating-red-dot {
  overflow: visible;
}

.ex-readiness-badge .proficiency-badge-mini .ex-readiness-badge__given-square.prop-custom-bedge,
.ex-readiness-badge .proficiency-badge-mini .ex-readiness-badge__pulse-dot.prop-custom-bedge {
  position: relative;
  left: 2px;
}

.ex-readiness-badge .prop-status-label {
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0em;
  text-align: center;
}

.ex-readiness-badge .badge-cross-icon {
  padding: 2px;
  border-radius: 50%;
  cursor: pointer;
}

.ex-readiness-badge .novice-btn {
  width: 57px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid #7F56D9;
  background-color: #7F56D9;
  box-shadow: 0px 1px 2px 0px #1018280D;
  position: relative;
  left: 14px;
  top: 0px;
  text-align: center;
  cursor: pointer;
}

.ex-readiness-badge .novice-plus {
  position: relative;
  top: -1px;
}

.ex-readiness-badge .novice-badge {
  position: relative;
  top: 1px;
}

.ex-readiness-badge .badge-icon {
  position: relative;
  padding-left: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -1px;
  gap: 6px;
  overflow: visible;
}

.ex-readiness-badge .ex-badge-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  overflow: visible;
  margin-left: 5px;
}

.chatterIcon-zero > svg {
  position: relative;
  top: -1px;
}

.ex-readiness-badge .proficiency-badge-plain--apprentice {
  border-radius: 6px !important;
  border: 1px solid;
  background-color: #FFF5D3 !important;
  border-color: #E58700 !important;
  width: 24px;
  height: 24px !important;
}

.ex-readiness-badge .proficiency-badge-plain--proficient {
  border-radius: 6px !important;
  border: 1px solid;
  background-color: #F3FFF9 !important;
  border-color: #00B813 !important;
  width: 24px;
  height: 24px !important;
}

.ex-readiness-badge .proficiency-badge-plain--coach {
  border-radius: 6px !important;
  border: 1px solid;
  background-color: #EFFEFF !important;
  border-color: #00A3FF !important;
  width: 24px;
  height: 24px !important;
}

.ex-readiness-badge .proficiency-badge-plain--architect {
  border-radius: 6px !important;
  border: 1px solid;
  background-color: #F2F3FF !important;
  border-color: #2E1EC1 !important;
  width: 24px;
  height: 24px !important;
}

.ex-readiness-badge .proficiency-badge-plain--novice {
  border-radius: 6px !important;
  border: 1px solid #D0D5DD;
  background-color: #FFFFFF !important;
  box-shadow: 0px 1px 2px 0px #1018280D;
  width: 24px;
  height: 24px !important;
}

.ex-readiness-badge .plain-badge-image {
  width: 20px;
  height: 20px;
}

.ex-readiness-badge .proficiency-badge-mini--apprentice {
  border-radius: 6px !important;
  border: 1px solid;
  background-color: #FFF5D3 !important;
  border-color: #E58700 !important;
  padding: 4px;
}

.ex-readiness-badge .proficiency-badge-mini--proficient {
  border-radius: 6px !important;
  border: 1px solid;
  background-color: #F3FFF9 !important;
  border-color: #00B813 !important;
  padding: 4px;
}

.ex-readiness-badge .proficiency-badge-mini--coach {
  border-radius: 6px !important;
  border: 1px solid;
  background-color: #EFFEFF !important;
  border-color: #00A3FF !important;
  padding: 4px;
}

.ex-readiness-badge .proficiency-badge-mini--architect {
  border-radius: 6px !important;
  border: 1px solid;
  background-color: #F2F3FF !important;
  border-color: #2E1EC1 !important;
  padding: 4px;
}

.ex-readiness-badge .proficiency-badge-mini {
  height: 24px !important;
}

.ex-readiness-badge .proficiency-badge-mini .prop-custom-bedge {
  position: relative;
  left: 2px;
}

.ex-readiness-badge .proficiency-badge-mini .badge-icon {
  padding-left: 1px;
}

.ex-readiness-badge .proficiency-badge-mini .badge-icon ._icon {
  width: 16px;
}

.ex-readiness-badge img.badge-cross-icon:hover {
  cursor: url('../assets/cursorArrow.svg'), auto;
}

.ex-readiness-badge .hover-cross-Apprentice:hover, .hover-cross-apprentice:hover {
  background-color: #E58700;
  box-shadow: 0px 4px 4px 0px #00000040;
}

.ex-readiness-badge .hover-cross-Proficient:hover, .hover-cross-proficient:hover {
  background-color: #00B813;
  box-shadow: 0px 4px 4px 0px #00000040;
}

.ex-readiness-badge .hover-cross-Coach:hover, .hover-cross-coach:hover {
  background-color: #00A3FF;
  box-shadow: 0px 4px 4px 0px #00000040;
}

.ex-readiness-badge .hover-cross-Architect:hover, .hover-cross-architect:hover {
  background-color: #2E1EC1;
  box-shadow: 0px 4px 4px 0px #00000040;
}

.ex-readiness-badge .readiness-view-proficiency-badge {
  margin: auto !important;
}

.ex-readiness-badge .readiness-view-proficiency-badge .v-chip {
  width: 120px !important;
  height: 24px !important;
}

.ex-readiness-badge .readiness-view-proficiency-badge .v-chip .v-chip__content {
  margin: auto !important;
}

.ex-readiness-badge .readiness-view-proficiency-badge .v-chip .v-chip__content .el-tooltip,
.ex-readiness-badge .readiness-view-proficiency-badge .v-chip .v-chip__content .el-tooltip > div:first-child {
  display: flex;
  justify-content: center;
  align-items: center;
}

.ex-readiness-badge .prop-custom-bedge-holder.v-badge--dot .v-badge__badge {
  width: 6px;
  height: 6px;
  inset: auto auto calc(100% - 3px) calc(100% - 5px) !important;
}

.ex-readiness-badge .ex-readiness-badge__given-square--holder.prop-custom-bedge-holder {
  display: inline-block;
  width: 6px;
  height: 6px;
  min-width: 6px;
  box-sizing: border-box;
  background-color: #000000;
  border-radius: 0;
  flex-shrink: 0;
  align-self: center;
}

.ex-readiness-badge .ex-readiness-badge__pulse-dot--holder.prop-custom-bedge-holder {
  display: inline-flex;
  flex-shrink: 0;
  align-self: center;
  line-height: 0;
  overflow: visible;
}

.ex-readiness-badge  { 
  width: fit-content !important;
}

.ex-readiness-badge .holder-badge--Apprentice {
  border-radius: 16px !important;
  border: 1px solid !important;
  border-color:#E58700 !important;
  background-color: #FFF3E0 !important;
  height: 32px;
  display: inline-flex;
  align-items: center;
  column-gap: 7px;
  padding: 0px 8px 0px 14px;
}

.ex-readiness-badge .holder-badge--Proficient {
  border-radius: 16px !important;
  border: 1px solid !important;
  border-color:#00B813 !important;
  background-color: #EFFFEF !important;
  height: 32px;
  display: inline-flex;
  align-items: center;
  column-gap: 7px;
  padding: 0px 8px 0px 14px;
}

.ex-readiness-badge .holder-badge--Coach {
  border-radius: 16px !important;
  border: 1px solid !important;
  border-color: #00A3FF !important;
  background-color: #EFFEFF !important;
  height: 32px;
  display: inline-flex;
  align-items: center;
  column-gap: 7px;
  padding: 0px 8px 0px 14px;
}

.ex-readiness-badge .holder-badge--Architect {
  border-radius: 16px !important;
  border: 1px solid !important;
  border-color: #2E1EC1 !important;
  background-color: #F2F3FF !important;
  height: 32px;
  display: inline-flex;
  align-items: center;
  column-gap: 7px;
  padding: 0px 8px 0px 14px;
}

.ex-readiness-badge .holder-text--Apprentice {
  color: #E58700 !important;
}

.ex-readiness-badge .holder-text--Proficient {
  color: #00B813 !important;
}

.ex-readiness-badge .holder-text--Coach {
  color: #00A3FF !important;
}

.ex-readiness-badge .holder-text--Architect {
  color: #2E1EC1 !important;
}

.ex-readiness-badge .holder-status-label {
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0em;
  text-align: center;
}

.ex-readiness-badge .holder-cross-icon {
  padding: 2px;
  border-radius: 50%;
  cursor: pointer;
  height: 16px;
  width: 16px;
}

.ex-readiness-badge .holder-cross-icon:hover {
    cursor: url('../assets/cursorArrow.svg'), auto;
}

.ex-readiness-badge .hover-cross-Apprentice:hover {
  background-color: #E58700;
  box-shadow: 0px 4px 4px 0px #00000040;
}

.ex-readiness-badge .hover-cross-Proficient:hover {
  background-color: #00B813;
  box-shadow: 0px 4px 4px 0px #00000040;
}

.ex-readiness-badge .hover-cross-Coach:hover {
  background-color: #00A3FF;
  box-shadow: 0px 4px 4px 0px #00000040;
}

.ex-readiness-badge .hover-cross-Architect:hover {
  background-color: #2E1EC1;
  box-shadow: 0px 4px 4px 0px #00000040;
}

.ex-readiness-badge .ex-holder-badge-icon {
  margin-top: -2px;
}

.ex-readiness-badge .pro-holder-badge-content {
  display: flex;
  align-items: center;
  top: 3px;
}

.ex-readiness-badge .pro-holder-badge-info {
  display: flex;
  align-items: center;
}

.ex-readiness-badge .pro-holder-badge .custom-avatar {
  height: 16px !important;
  min-width: 16px !important;
  width: 16px !important;
  top: 0px;
}

.ex-readiness-badge .status {
  background-color: #666666 !important;
  border: 1px solid #000000 !important;
}

.ex-readiness-badge .pro-holder-badge .v-btn--icon.v-size--default {
  height: 18px;
  width: 18px;
}

.ex-readiness-badge .truncated-badge-name {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 150px;
}
</style>

<style scoped>
::v-deep(.v-chip) {
  line-height: normal !important;
}

:v-deep(.chatter-icon-com) {
  margin-top: -1px !important;
}

::v-deep(.chatter-icon-com.chatterIcon-unread div .unread-badge) {
  margin: 1px -1px;
}

::v-deep(.chatter-icon-com.is-white.chatterIcon-unread div .unread-badge) {
  margin: -1px -1px !important;
}

::v-deep(.proficiency-badge-mini .chatter-icon-com svg) {
  height: 15px !important;
  width: 13px !important;
  top: -2px !important;
}

.pro-holder-badge-content  {
  position: relative;
  left: 8px;
  top: 0px;
}

.pro-holder-badge-content .prop-custom-bedge-holder  {
  left: -5px;
  position: relative;
}

::v-deep(.chatter-icon-com.chatterIcon-zero) {
  padding: 0px !important;
}
::v-deep(.chatter-icon-com.chatterIcon-zero:hover) {
  padding: 0px !important;
}

::v-deep(.chatter-icon-com.is-white.chatterIcon-zero) {
  background-color: transparent !important;
  margin-top: -5px !important;
  width: 25px;
  height: 25px;
  position: relative;
  left: 3px;
}
::v-deep(.chatter-icon-com.is-white.chatterIcon-zero div > svg) {
  top: 3px !important;
}
::v-deep(.chatter-icon-com.is-white.chatterIcon-unread) {
  margin-top: -5px !important;
}

::v-deep(.chatter-icon-com.is-white.chatterIcon-unread) {
  padding: 4px 4px 0px 4px !important;
}
::v-deep(.chatter-icon-com.is-white.chatterIcon-unread:hover) {
  padding: 4px 4px 0px 4px !important;
}

::v-deep(.chatter-icon-com.chatterIcon-unread) {
  padding: 0px 3px 0px 3px !important;
}
::v-deep(.chatter-icon-com.chatterIcon-unread:hover) {
  padding: 0px 3px 0px 3px !important;
}

::v-deep(.pro-holder-badge-content .chatter-icon-com) {
  padding: 2px 2px 0px 2px !important;
}
::v-deep(.pro-holder-badge-content .chatter-icon-com:hover) {
  padding: 2px 2px 0px 2px !important;
}

::v-deep(.pro-holder-badge-content .chatterIcon-zero div > svg ) {
  position: relative;
  top: 1px !important;
  left: 0px;
}

::v-deep(.pro-holder-badge-content .chatterIcon-unread div > svg ) {
  position: relative;
  top: 1px;
  left: 0px;
}

::v-deep(.pro-holder-badge-content .chatterIcon-unread div .unread-badge  ) {
  top: -3px;
  right: -2px;
}

::v-deep(.pro-holder-badge .avail-close-btn ) {
  margin-left: 5px !important;
}

.pro-holder-badge-info {
  margin-right: 5px !important;
}

::v-deep(.pro-holder-badge .custom-avatar){
  margin: 0px 5px !important;
}
</style>